import Icons, { IconSize } from '@pdcfrontendui/components/Icons';
import {
  MessageViewQueries,
  getMessageRouteParams,
  getSiteRoutes,
  useMessageRouteParams,
  MessageViewTab,
} from '../routes';
import { ModalType, ModalWrapperProps } from '../Modal/ModalWrapper';
import React, { useCallback, useMemo } from 'react';
import { dateMath } from '@pdcfrontendui/utils';
import {
  countUnreadMessages,
  dayMessageToMessage,
  hasUnreadMessages,
  shouldShowAsUnread,
} from '../Chat/chatUtils';

import { DayMessage } from '@pdcplan/shared/api/Message';
import { DayMessageMap, getChatKey } from '../util/getChatKey';
import { EmployeeMap } from '../api/model';
import Header from '@pdcfrontendui/components/Header';
import { MsgVisibilityOldEnum } from '@pdcplan/shared/enums';
import NoMessages from '../components/NoMessages';
import { Period } from '../util/dates';
import Tabs from '@pdcfrontendui/components/Tabs';
import classNames from 'classnames';
import { currentLanguage } from '../currentLanguage';
import ids from '../testing/ids';
import { isWithinInterval } from 'date-fns';
import scss from './MessageView.module.scss';
import { useIsDualView } from '@pdcfrontendui/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BadgeVariant,
  ChatList,
  type ChatListItem,
} from '@pdcfrontendui/components';
import { NoSelectedMessage } from '../components/NoSelectedMessage';
import Chat from '../Chat';
import { useFormatter } from '../hooks/useLang';

export type StateFromProps = {
  dayMessageMap: DayMessageMap;
  employees: EmployeeMap;
  userId: number;
  teamId: string;
  currentDate: Date;
  period: Period;
  activeScope: number;
  canSeeConfidentialInfo: boolean;
};

export type DispatchFromProps = {
  openModal: (modal: ModalWrapperProps) => void;
  callPostMessage: (
    personId: number,
    messageText: string,
    isInternalMessage: boolean,
    day: Date,
    sentWhen: Date
  ) => void;
};

const MessageView = ({
  dayMessageMap,
  employees,
  userId,
  teamId,
  currentDate,
  openModal,
  callPostMessage,
  period,
  activeScope,
  canSeeConfidentialInfo,
}: StateFromProps & DispatchFromProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const screensizeBig = useIsDualView();
  const formatter = useFormatter();
  const listViewClassName = screensizeBig ? 'splitpane-left' : 'splitpane-one';
  const {
    tab = canSeeConfidentialInfo ? MessageViewTab.Unread : MessageViewTab.Read,
    thread: chatKey,
  } = useMessageRouteParams();

  // Array of threads, sorted by last message sent
  const threads = useMemo(
    () =>
      Object.values(dayMessageMap)
        .filter((thread) =>
          isWithinInterval(thread[0].Day, {
            start: period.from,
            end: period.to,
          })
        )
        .sort(
          (a, b) =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            a[a.length - 1]!.SentWhen.getTime() -
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            b[b.length - 1]!.SentWhen.getTime()
        )
        .reverse(),
    [dayMessageMap, period.from, period.to]
  );
  const dayMessages = chatKey ? (dayMessageMap[chatKey] ?? null) : null;
  const message = chatKey ? dayMessages?.[0] : null;
  const personId = message?.PersonId ?? -1;
  const day = message?.Day ?? dateMath.BEGINNING_OF_TIME;
  const visibility = message?.Visibility ?? MsgVisibilityOldEnum.employee;
  const messages = useMemo(
    () =>
      (dayMessages ?? []).map((dayMessage) =>
        dayMessageToMessage(dayMessage, userId)
      ),
    [dayMessages, userId]
  );

  const sendMessage = useCallback(
    (text: string) => {
      callPostMessage(
        personId,
        text,
        visibility === MsgVisibilityOldEnum.planner,
        day,
        currentDate // TODO: Figure out sent time for temp message
      );
    },
    [callPostMessage, personId, visibility, day, currentDate]
  );

  const unreadCount = useMemo(
    () => countUnreadMessages(dayMessageMap, userId, period.from, period.to),
    [dayMessageMap, userId, period.from, period.to]
  );

  const messageTabsArr = [MessageViewTab.Unread, MessageViewTab.Read] as const;

  const updateParams = useCallback(
    (updates: MessageViewQueries) => {
      navigate(
        getSiteRoutes().chat(teamId, {
          ...getMessageRouteParams(location),
          ...updates,
        })
      );
    },
    [location, teamId, navigate]
  );

  const onGoBack = () => {
    updateParams({ thread: undefined });
  };

  const onThreadClick = useCallback(
    (message: DayMessage) => {
      updateParams({
        thread: getChatKey(message.PersonId, message.Day, message.Visibility),
      });
    },
    [updateParams]
  );

  const shownThreads = useMemo(
    () =>
      threads.filter(
        tab === MessageViewTab.Read
          ? (thread) => !hasUnreadMessages(thread, userId)
          : (thread) => hasUnreadMessages(thread, userId)
      ),
    [threads, tab, userId]
  );

  const items: ChatListItem[] = useMemo(
    () =>
      shownThreads.map((thread) => ({
        icon:
          thread[0].Visibility === MsgVisibilityOldEnum.planner
            ? 'ChatLock'
            : 'Chat',
        isSelected:
          getChatKey(
            thread[0].PersonId,
            thread[0].Day,
            thread[0].Visibility
          ) === chatKey,
        onClick: () => {
          onThreadClick(thread[0]);
        },
        title: employees[thread[0].PersonId]?.name ?? '',
        content: `${currentLanguage.RegardingShort} ${formatter.weekdayDayMonth(
          thread[0].Day
        )}`,
        time:
          dateMath.floorDay(currentDate).valueOf() ===
          dateMath.floorDay(thread[0].SentWhen).valueOf()
            ? formatter.time(thread[0].SentWhen, false)
            : formatter.dayMonthShortTime(thread[0].SentWhen, false),
        unreadCount: thread.filter((message) =>
          shouldShowAsUnread(message, userId)
        ).length,
        key: thread[0].MessageId,
      })),
    [
      shownThreads,
      employees,
      currentDate,
      onThreadClick,
      chatKey,
      userId,
      formatter,
    ]
  );

  return (
    <div className="splitpane">
      <div className={listViewClassName}>
        <Header centerize>
          <Header.Title bold>
            {currentLanguage.ChatAndInternalMessages}
          </Header.Title>
          <Header.Right>
            <Icons.Info
              size={IconSize.XSmall}
              onClick={() => {
                openModal({
                  modalType:
                    tab === MessageViewTab.Unread
                      ? ModalType.chatUnread
                      : ModalType.chatRead,
                  modalBackText: currentLanguage.Close,
                  content:
                    tab === MessageViewTab.Unread
                      ? currentLanguage.MessageModalUnread_1(activeScope)
                      : currentLanguage.MessageModalRead_1(activeScope),
                });
              }}
            />
          </Header.Right>
        </Header>
        {canSeeConfidentialInfo && (
          <Tabs
            tabsArr={messageTabsArr}
            selectedTab={tab}
            setSelectedTab={(tab) => {
              updateParams({ tab, thread: undefined });
            }}
            tabLabelMap={{
              [MessageViewTab.Unread]: currentLanguage.Unread,
              [MessageViewTab.Read]: currentLanguage.Read,
            }}
            badgeMap={{
              [MessageViewTab.Unread]: {
                count: unreadCount,
                variant: BadgeVariant.Default,
              },
            }}
            idMap={{
              [MessageViewTab.Unread]: ids.ShiftView.unreadMessagesTab,
              [MessageViewTab.Read]: ids.ShiftView.readMessagesTab,
            }}
          />
        )}
        {items.length === 0 ? (
          <NoMessages
            tab={tab}
            period={period}
            className={classNames(scss.NoMessagesContainer, {
              [scss.big ?? '']: screensizeBig,
            })}
          />
        ) : (
          <ChatList items={items} className={scss.MessageList} />
        )}
      </div>
      <div
        className="splitpane-right"
        style={{ display: !screensizeBig ? 'none' : undefined }}
      >
        {(!screensizeBig || !chatKey) && (
          <Header>{canSeeConfidentialInfo && <Header.Spacer />}</Header>
        )}
        {items.length > 0 && !chatKey && <NoSelectedMessage />}
        <Chat
          show={!!chatKey}
          messages={messages}
          sendMessage={sendMessage}
          canSeeConfidentialInfo={canSeeConfidentialInfo}
          title={
            visibility === MsgVisibilityOldEnum.public
              ? `${currentLanguage.ChatWith_1(
                  employees[personId]?.name.split(' ')[0] ??
                    currentLanguage.Employee
                )}, ${formatter.weekdayDayMonth(day)}`
              : `${currentLanguage.InternalMessages}, ${formatter.weekdayDayMonth(
                  day
                )}`
          }
          goBack={screensizeBig ? undefined : onGoBack}
          noSlideInOnDesktop
          currentDay={currentDate}
          isChatPage={true}
        />
      </div>
    </div>
  );
};
export default MessageView;
